import React, { forwardRef, useImperativeHandle, useState } from 'react'
import {
    useStripe,
    useElements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    AddressElement,
} from '@stripe/react-stripe-js'

const ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': { color: '#aab7c4' },
        },
        invalid: { color: '#9e2146' },
    },
}

/**
 * Inner Stripe card form rendered inside an <Elements> provider.
 * Exposes createPaymentMethod() and clearElements() via ref.
 */
const StripeNewCardForm = forwardRef(function StripeNewCardForm(_props, ref) {
    const stripe = useStripe()
    const elements = useElements()

    const [cardError, setCardError] = useState('')
    const [addressData, setAddressData] = useState({})
    const [addressComplete, setAddressComplete] = useState(false)

    useImperativeHandle(ref, () => ({
        async createPaymentMethod() {
            if (!stripe || !elements) {
                return { error: { message: 'Stripe is not loaded yet.' } }
            }
            if (!addressComplete) {
                return { error: { message: 'Please complete the billing address.' } }
            }
            const cardNumberElement = elements.getElement(CardNumberElement)
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: addressData.name || '',
                    address: {
                        line1: addressData.address?.line1 || '',
                        line2: addressData.address?.line2 || '',
                        city: addressData.address?.city || '',
                        state: addressData.address?.state || '',
                        postal_code: addressData.address?.postal_code || '',
                        country: addressData.address?.country || '',
                    },
                },
            })
            if (error) {
                setCardError(error.message)
            }
            return { error, paymentMethod }
        },
        clearElements() {
            elements?.getElement(CardNumberElement)?.clear()
            elements?.getElement(CardExpiryElement)?.clear()
            elements?.getElement(CardCvcElement)?.clear()
            setAddressData({})
            setAddressComplete(false)
            setCardError('')
        },
    }))

    return (
        <div className="stripeNewCardForm">
            <div className="form-group">
                <label className="stripeLabel">Card Number</label>
                <div className="stripeElementWrapper">
                    <CardNumberElement
                        options={ELEMENT_OPTIONS}
                        onChange={(e) => setCardError(e.error ? e.error.message : '')}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-md-6 form-group">
                    <label className="stripeLabel">Expiry Date</label>
                    <div className="stripeElementWrapper">
                        <CardExpiryElement
                            options={ELEMENT_OPTIONS}
                            onChange={(e) => setCardError(e.error ? e.error.message : '')}
                        />
                    </div>
                </div>
                <div className="col-md-6 form-group">
                    <label className="stripeLabel">CVC</label>
                    <div className="stripeElementWrapper">
                        <CardCvcElement
                            options={ELEMENT_OPTIONS}
                            onChange={(e) => setCardError(e.error ? e.error.message : '')}
                        />
                    </div>
                </div>
            </div>
            {cardError && <div className="stripeError">{cardError}</div>}
            <div className="form-group mt-3">
                <label className="stripeLabel">Billing Address</label>
                <AddressElement
                    options={{
                        mode: 'billing',
                        fields: { phone: 'never' },
                        defaultValues: {
                            address: { country: global.defaultCountry || 'US' },
                        },
                    }}
                    onChange={(event) => {
                        setAddressData(event.value)
                        setAddressComplete(event.complete)
                    }}
                />
            </div>
        </div>
    )
})

export default StripeNewCardForm
